import React from 'react';
import PropTypes from 'prop-types';

import { Banner } from '@zillow/constellation';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { logError } from '@zg-rentals/log-error';

const ERROR_MESSAGE = {
  FAILED_TO_EXECUTE_REMOVE_CHILD: `Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.`,
  OBJECT_NOT_FOUND: 'The object can not be found here.',
};

const checkForMatch = (arr, val) => {
  return arr?.some?.((arrVal) => val === arrVal);
};

export class ErrorBoundary extends React.Component {
  state = { hasError: false };

  componentDidCatch(error, errorInfo) {
    this.setState({ hasError: true });

    /**
     * error is coming from a third party vendor and not easily reproducible.
     * adding some useragent information to the message in order to get better insight
     */
    const valuesList = Object.values(ERROR_MESSAGE);
    const foundMatchingErrorMessage = checkForMatch(valuesList, error?.message);

    if (foundMatchingErrorMessage) {
      logError({
        error,
        // differentiate these from normal error boundary errors so metric alerts can use `browser errorboundary total`
        errorType: 'ErrorBoundary_ThirdPartyError',
        context: {
          errorInfo,
        },
      });
    } else {
      RpErrorBoundary.logError(error, errorInfo);
    }
  }

  render() {
    const { children, errorView } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return errorView;
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  errorView: PropTypes.node,
};

ErrorBoundary.defaultProps = {
  errorView: (
    <Banner
      appearance="error"
      body="Oops - something went wrong, please refresh the page and try again!"
      onClose={() => window.location.reload()}
    />
  ),
};

export const withErrorBoundary = (WrappedComponent) => (props) => (
  <ErrorBoundary errorView={props.errorView}>
    <WrappedComponent {...props} />
  </ErrorBoundary>
);

export default ErrorBoundary;
